/* eslint-disable */
import { useEffect, useCallback, useRef, useState, useMemo } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import DoNotDisturbOnIcon from '@mui/icons-material/DoNotDisturbOn';
import { Button } from '@mui/material';
import SelectPicker from 'rsuite/SelectPicker';
import Input from 'rsuite/Input';
import Pagination from 'rsuite/Pagination';
import { ClipboardPaste } from 'lucide-react';
// import ListTable from '../../../Components/ListTable/ListTable';
import styles from './VariantsThresholdListPage.module.scss';
import { notificationServices } from '../../../Services';
import variantsThresholdServices from '../../../Services/variantsThreshold.services';
import assetServices from '../../../Services/AssetServices/asset.services';
import processFlowServices from '../../../Services/Analytics Services/process-flow.services';
import { BackdropLoader } from '../../../Components/Loaders';
import UploadDataModal from '../../../Components/Modals/UploadDataModal/UploadDataModal';
// eslint-disable-next-line import/no-unresolved, import/extensions
// import { VirtualizedListTable } from '@/Components/VirtualizedListTable';
import { ListTable } from '@/Components/ListTable';
import {
	skuTypesList,
	useProductData,
	parsePastedTable,
	matchPastedRowToProduct,
	cleanPastedName,
} from '@/Helpers';
import { matchIngredientToProduct } from '@/Components/Modals/MultiRecipeEditModal/MultiRecipeEditModal';
import { formActions, modalActions } from '@/Actions';
import { useAuth } from '@/Components/Auth/Login/auth';
import authActions from '@/Actions/auth.actions';
import PasteThresholdReviewModal from './PasteThresholdReviewModal';

const TABLE_COLUMNS = [
	{
		title: 'UY Variant ID',
		config: {
			type: 'DATA',
		},
	},
	{
		title: 'SkuCode',
		config: {
			type: 'DATA',
		},
	},
	{
		title: 'Name',
		config: {
			type: 'DATA',
		},
	},
	{
		title: 'SKU Type',
		config: {
			type: 'DATA',
		},
	},
	{
		title: 'Category',
		config: {
			type: 'DATA',
		},
	},
	{
		title: 'Sub Category',
		config: {
			type: 'DATA',
		},
	},
	{
		title: 'Unit',
		config: {
			type: 'DATA',
		},
	},
];
const SPECIAL_COLUMNS = [
	{
		title: 'Threshold',
		config: {
			type: 'DATA',
		},
	},
	{
		title: 'Max Stock',
		config: {
			type: 'DATA',
		},
	},
	{
		title: 'Recommended Stock',
		config: {
			type: 'DATA',
		},
	},
];
const ACTION_COLUMNS = [
	// {
	// title: 'View',
	// config: {
	// type: 'VIEW',
	// },
	// },
];

const COLMAP = {
	'UY Variant ID': 'variantId',
	SkuCode: 'clientVariantId',
	Name: 'productNameDisplay',
	'SKU Type': 'skuType',
	'Sub Category': 'subCategoryName',
	Category: 'category',
	Unit: 'unit',
	Threshold: 'uniqueKey',
	'Max Stock': 'uniqueKey',
	'Recommended Stock': 'uniqueKey',
	'Asset Name': 'assetName',
	'Asset Id': 'assetId',
};

const skuTypes = ['ALL', ...skuTypesList];

// The grid is a window over the server's rows now, not the whole store.
const DEFAULT_PAGE_SIZE = 200;
const PAGE_SIZE_OPTIONS = [50, 100, 200, 500];

// /threshold/list used to answer with a bare array and now answers with
// { items, pagination }. Both shapes are read through here rather than being
// assumed anywhere, because the two sides of that change do not deploy at the
// same instant: a console serving the pre-pagination bundle against the new
// API crashed on `data.forEach is not a function` (the object has no forEach),
// and the mirror case — new bundle, old/rolled-back API — is worse than a
// crash, silently rendering an empty grid over a store that does have rows.
// Anything unrecognised degrades to an empty list rather than throwing.
const toListRows = (payload) => {
	if (Array.isArray(payload?.items)) return payload.items;
	if (Array.isArray(payload)) return payload;
	return [];
};
// Long enough to stop firing a query per keystroke, short enough that the list
// still feels like it is reacting to typing.
const SEARCH_DEBOUNCE_MS = 400;

// Threshold / stock values are stored in base units (grams / ml) in the
// backend, but shown to the user in Kg / Ltr for weight & volume SKUs.
const WEIGHT_UNITS = ['gram', 'grams', 'gm', 'g'];
const VOLUME_UNITS = ['ml', 'milliliter', 'millilitre'];

const getUnitMeta = (unit) => {
	const normalized = (unit ?? '').toString().trim().toLowerCase();
	if (WEIGHT_UNITS.includes(normalized)) {
		return { convert: true, label: 'Kg' };
	}
	if (VOLUME_UNITS.includes(normalized)) {
		return { convert: true, label: 'Ltr' };
	}
	return { convert: false, label: unit };
};

// Avoids floating point artifacts (e.g. 1.234 * 1000 => 1234.0000000002).
const roundValue = (num, precision = 4) => {
	const factor = 10 ** precision;
	return Math.round(num * factor) / factor;
};

// base (g / ml) -> display (Kg / Ltr). Display values are capped at 3 decimal
// places (0.001 Kg / Ltr == 1 g / ml).
const toDisplayValue = (value, unit) => {
	if (value === null || value === undefined || value === '') return value;
	const num = Number(value);
	if (Number.isNaN(num)) return value;
	return getUnitMeta(unit).convert ? roundValue(num / 1000, 3) : num;
};

// display (Kg / Ltr) -> base (g / ml)
const toBaseValue = (value, unit) => {
	if (value === null || value === undefined || value === '') return value;
	const num = Number(value);
	if (Number.isNaN(num)) return value;
	return getUnitMeta(unit).convert ? roundValue(num * 1000) : num;
};

// The upload template carries a "Unit" column with the DISPLAY label
// (Kg / Ltr). Values under Kg / Ltr must be converted back to base units
// (g / ml) before uploading; values already in g / ml are left untouched.
const KG_UNITS = ['kg', 'kgs', 'kilogram', 'kilograms', 'kilo'];
const LTR_UNITS = ['ltr', 'ltrs', 'litre', 'litres', 'liter', 'liters', 'l'];
const isDisplayWeightVolumeUnit = (unitLabel) => {
	const u = (unitLabel ?? '').toString().trim().toLowerCase();
	return KG_UNITS.includes(u) || LTR_UNITS.includes(u);
};

// Reads the base64 CSV (data URL) uploaded for "Update Threshold", and for any
// row whose Unit is Kg / Ltr converts the Threshold / Recommended Stock / Max
// Stock values to g / ml (x1000). Rows already in g / ml (or any other unit)
// are left unchanged. Returns the original input untouched if it can't be
// parsed or has no Unit column.
const convertUploadedThresholdCsv = (fileData) => {
	if (typeof fileData !== 'string' || fileData === '') return fileData;
	const isDataUrl = fileData.startsWith('data:');
	let prefix = '';
	let b64 = fileData;
	if (isDataUrl) {
		const sep = fileData.indexOf(',');
		if (sep === -1) return fileData;
		prefix = fileData.slice(0, sep + 1);
		b64 = fileData.slice(sep + 1);
	}
	let text;
	try {
		text = decodeURIComponent(escape(atob(b64)));
	} catch (e) {
		try {
			text = atob(b64);
		} catch (e2) {
			return fileData;
		}
	}
	const lines = text.split(/\r?\n/);
	let headerIdx = -1;
	for (let i = 0; i < lines.length; i += 1) {
		if (lines[i].trim() !== '') {
			headerIdx = i;
			break;
		}
	}
	if (headerIdx === -1) return fileData;
	const header = lines[headerIdx]
		.split(',')
		.map((h) => h.trim().toLowerCase());
	const unitIdx = header.indexOf('unit');
	if (unitIdx === -1) return fileData;
	const valueCols = ['threshold', 'recommended stock', 'max stock']
		.map((c) => header.indexOf(c))
		.filter((idx) => idx !== -1);
	if (valueCols.length === 0) return fileData;
	for (let i = headerIdx + 1; i < lines.length; i += 1) {
		if (lines[i].trim() === '') continue;
		const leading = lines[i].match(/^\s*/)[0];
		const cells = lines[i].slice(leading.length).split(',');
		if (isDisplayWeightVolumeUnit(cells[unitIdx])) {
			valueCols.forEach((idx) => {
				const cell = cells[idx];
				if (cell !== undefined && cell.trim() !== '') {
					const num = Number(cell.trim());
					if (!Number.isNaN(num)) {
						cells[idx] = String(roundValue(num * 1000));
					}
				}
			});
		}
		lines[i] = leading + cells.join(',');
	}
	const newText = lines.join('\n');
	try {
		const newB64 = btoa(unescape(encodeURIComponent(newText)));
		return isDataUrl ? prefix + newB64 : newB64;
	} catch (e) {
		return fileData;
	}
};

/* -------------------------------------------------------------------------- */
/* Excel / clipboard paste                                                    */
/* -------------------------------------------------------------------------- */

// The three editable columns, in the order they appear in the grid.
const PASTE_FIELDS = ['threshold', 'maxStock', 'recommendedStock'];

// Header aliases per column. `threshold` is the primary column parsePastedTable
// returns as `qty`; the other two ride along as extra columns.
const PASTE_THRESHOLD_HEADERS = ['threshold', 'threshold / min stock', 'min stock', 'minstock'];
const PASTE_EXTRA_COLUMNS = [
	{ key: 'maxStock', aliases: ['max stock', 'maxstock'] },
	{
		key: 'recommendedStock',
		aliases: ['recommended stock', 'recommendedstock', 'recommend stock', 'recommendstock'],
	},
];
// This page's export calls the SKU column "Item Code"; the grid header says
// "SkuCode". Both must resolve to the SKU column.
const PASTE_SKU_HEADERS = ['item code', 'itemcode'];

// A pasted "Unit" cell is read against the row's own unit. Sheets exported from
// this page carry the DISPLAY label (Kg / Ltr) so the value needs no change;
// a sheet still in base units (g / ml) is scaled down to the display unit.
const PASTED_BASE_UNITS = [
	'g', 'gm', 'gms', 'gram', 'grams',
	'ml', 'milliliter', 'millilitre', 'milliliters', 'millilitres',
];

const toDisplayFromPastedUnit = (value, pastedUnit, rowUnit) => {
	const num = Number(value);
	if (value === null || value === undefined || value === '' || !Number.isFinite(num)) {
		return null;
	}
	// Count / piece SKUs are shown as-is, so there is nothing to convert.
	if (!getUnitMeta(rowUnit).convert) return roundValue(num, 3);
	const pasted = String(pastedUnit ?? '').trim().toLowerCase();
	if (PASTED_BASE_UNITS.includes(pasted)) return roundValue(num / 1000, 3);
	// Blank / Kg / Ltr / anything else => already the unit shown in the grid.
	return roundValue(num, 3);
};

// eslint-disable-next-line no-unused-vars
const generateInput = (value, onInputChange, isPasted = false) => {
	return (
		<div className={styles['variant-input']}>
			<input
				className={`${styles.thres_input ?? ''} ${
					isPasted ? styles.pasted : ''
				}`}
				type='number'
				value={value}
				onChange={onInputChange}
			/>
		</div>
	);
};

const VariantsThresholdListPage = (props) => {
	const {
		fetchVariantsThreshold,
		data,
		stores,
		fetchStoresList,
		updateVariantsThresholdList,
		toggleUploadDataModal,
		showUploadDataModal,
		payload,
		// 'STORE' (default) drives the store/asset scoped page. 'PROCESS' swaps
		// the asset picker for a process picker and scopes every read and write
		// to ProcessProductVariantThreshold instead.
		thresholdLevel = 'STORE',
	} = props;
	const isProcess = thresholdLevel === 'PROCESS';
	// /threshold/list returns one page plus its counts; everything below works
	// off the page's rows.
	const listItems = useMemo(() => {
		return toListRows(data);
	}, [data]);
	const totalRows = data?.pagination?.total ?? 0;
	// A labelled page-size list (rsuite's own limit dropdown formats every
	// entry through one shared "{n} / page" template, so an "All" option needs
	// its own SelectPicker instead). Appending the live total — rather than a
	// hardcoded number — is also what makes it possible to view/paste against
	// the whole filtered list instead of just the current page.
	const limitSelectData = useMemo(() => {
		const items = PAGE_SIZE_OPTIONS.map((size) => ({
			label: `${size} / page`,
			value: size,
		}));
		if (totalRows > 0 && !PAGE_SIZE_OPTIONS.includes(totalRows)) {
			items.push({ label: `All (${totalRows})`, value: totalRows });
		}
		return items;
	}, [totalRows]);
	const ENTITY = isProcess ? 'PROCESS_THRESHOLD' : 'VARIANTS_THRESHOLD';
	const listRef = useRef(null);
	const [searchQuery, setSearchQuery] = useState('');
	const [selectedStore, setSelectedStore] = useState(null);
	const [selectedSkuType, setSelectedSkuType] = useState(null);
	const [updateState, setUpdateState] = useState({});
	const [assetId, setAssetId] = useState(null);
	const [assetsList, setAssetsList] = useState([]);
	const [assetsLoading, setAssetsLoading] = useState(false);
	const [processId, setProcessId] = useState(null);
	const [processList, setProcessList] = useState([]);
	const [processLoading, setProcessLoading] = useState(false);
	// { [uniqueKey]: { threshold: true, ... } } — cells filled by the last paste,
	// so the user can see what changed before hitting Submit.
	const [pastedCells, setPastedCells] = useState({});
	// Set when a paste needs the user's input (ambiguous / unmatched names).
	const [pasteReview, setPasteReview] = useState(null);
	// { [ambiguousIndex]: variantId } picked in the review modal.
	const [pasteResolutions, setPasteResolutions] = useState({});
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
	// Search runs on the server, so it is debounced rather than applied to a
	// list already in memory.
	const [debouncedSearch, setDebouncedSearch] = useState('');
	const [isLoading, setIsLoading] = useState(false);
	const { userConfig = {} } = useAuth();
	const { level } = userConfig?.userDetails || {};
	const { userRolesDict = {} } = userConfig || {};
	const isInternal =
		(Object?.values?.(userRolesDict ?? {}).includes('INTERNAL') ||
			Object?.values?.(userRolesDict ?? {}).includes('Internal')) &&
		[2, 3, 4].includes(level);
	const nonInternal =
		!Object?.values?.(userRolesDict ?? {}).includes('INTERNAL') &&
		!Object?.values?.(userRolesDict ?? {}).includes('Internal') &&
		[3, 4].includes(level);
	const products = useSelector(
		(state) => state?.form?.ALL_PRODUCTS?.data?.LIST
	);
	const dispatch = useDispatch();

	useEffect(() => {
		if (Array.isArray(products) && products?.length === 0) {
			useProductData(dispatch);
		}
	}, [products]);

	useEffect(() => {
		setIsLoading(true);
		fetchStoresList({
			entity: 'STORES',
			req: {
				params: {
					id: '',
				},
			},
			method: 'LIST',
		});
		setIsLoading(false);
		return () => {
			updateVariantsThresholdList({
				entity: ENTITY,
				method: 'LIST_DATA',
				innerField: 'data',
				data: null,
			});
		};
	}, []);

	useEffect(() => {
		const handle = setTimeout(() => {
			setDebouncedSearch(searchQuery?.trim() ?? '');
		}, SEARCH_DEBOUNCE_MS);
		return () => {
			return clearTimeout(handle);
		};
	}, [searchQuery]);

	// The one scope param that separates the two pages. Every read and write
	// carries it, so a process threshold can never be saved against the store.
	const scopeParams = useMemo(() => {
		if (isProcess) return processId ? { processId } : {};
		return assetId ? { assetId } : {};
	}, [isProcess, processId, assetId]);

	// Fetch assets list from API
	const fetchAssetsListData = async (storeId) => {
		if (!storeId) return;
		setAssetsLoading(true);
		try {
			const result = await assetServices.assetProductInventoryService({
				req: { data: { storeId, type: 'Display Freezer' } },
			});
			if (result?.status === 'Success' && result?.data?.assets) {
				const formattedAssets = result.data.assets.map((asset) => ({
					value: asset.id,
					label: asset.name,
				}));
				setAssetsList(formattedAssets);
			} else {
				setAssetsList([]);
			}
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error('Error fetching assets:', error);
			setAssetsList([]);
		} finally {
			setAssetsLoading(false);
		}
	};

	// Processes of the selected store — same source as the PO screens'
	// From/To Process pickers (ProcessFlowNodes via the dashboard service).
	const fetchProcessListData = async (storeId) => {
		if (!storeId) return;
		setProcessLoading(true);
		try {
			const result = await processFlowServices.fetchNodes({
				params: { id: '', storeId },
			});
			const nodes = result?.data?.nodes ?? [];
			setProcessList(
				nodes.map((node) => {
					const section = node?.data?.sectionName;
					return {
						value: node?.id,
						label: `${node?.data?.heading}${
							section ? ` (Section: ${section})` : ''
						}`,
					};
				})
			);
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error('Error fetching processes:', error);
			setProcessList([]);
		} finally {
			setProcessLoading(false);
		}
	};

	useEffect(() => {
		if (!selectedStore) return;
		if (isProcess) {
			setProcessId(null);
			setProcessList([]);
			fetchProcessListData(selectedStore);
			return;
		}
		setAssetId(null);
		setAssetsList([]);
		fetchAssetsListData(selectedStore);
	}, [selectedStore, isProcess]);

	// Everything that decides WHICH rows the server should return. Page number
	// is deliberately not part of it - it is the window, not the query.
	const listParams = useMemo(() => {
		return {
			storeId: selectedStore,
			productType:
				!selectedSkuType || selectedSkuType === 'ALL'
					? null
					: selectedSkuType,
			...scopeParams,
			...(debouncedSearch ? { search: debouncedSearch } : {}),
		};
	}, [selectedStore, selectedSkuType, scopeParams, debouncedSearch]);

	const listSignature = JSON.stringify(listParams) + `|${pageSize}`;
	const lastSignature = useRef(null);

	useEffect(() => {
		// A process threshold list is meaningless without a process — without
		// one the API would fall back to the store table and show the wrong
		// numbers, so wait for the picker.
		// SKU Type is NOT waited on. It used to be, because an unfiltered list
		// meant loading every variant of the store in one response; the server
		// pages now, so leaving it unset is just "no skuType filter".
		if ((isProcess && !processId) || !selectedStore) {
			updateVariantsThresholdList({
				entity: ENTITY,
				method: 'LIST_DATA',
				innerField: 'data',
				data: null,
			});
			return;
		}

		// A changed filter or search invalidates the page number: page 7 of a
		// new, shorter result set is an empty grid. Reset and let the resulting
		// render do the fetching, so the stale page is never requested.
		const queryChanged = lastSignature.current !== listSignature;
		lastSignature.current = listSignature;
		if (queryChanged && page !== 1) {
			setPage(1);
			return;
		}

		updateVariantsThresholdList({
			entity: ENTITY,
			method: 'LIST_DATA',
			innerField: 'data',
			data: null,
		});
		fetchVariantsThreshold({
			entity: ENTITY,
			req: {
				params: {
					...listParams,
					page,
					limit: pageSize,
				},
			},
			method: 'LIST_DATA',
			showNotification: true,
		});
	}, [listSignature, page]);

	// eslint-disable-next-line no-unused-vars
	const flattenedData = useMemo(() => {
		const result = [];
		listItems?.forEach((product) => {
			product?.variants?.forEach((variant) => {
				result.push({
					...product,
					...variant,
					subCategoryName:
						products?.find((ele) => ele?.id === product?.id)
							?.subCategoryName || product?.subCategoryName,
					productNameDisplay: `${product?.productName} (${variant?.variantName})`,
					uniqueKey: `${product?.id}__${variant?.variantId}`, // Double underscore separator
				});
			});
		});
		return result;
	}, [data, products]);

	// No client-side search any more - the server returned exactly the rows
	// that match. This only decorates them with the selected asset.
	const filteredData = useMemo(() => {
		const assetData = assetsList?.find((ele) => ele?.value === assetId);
		return flattenedData.map((item) => {
			return {
				...item,
				assetId: assetId,
				assetName: assetData?.label,
			};
		});
	}, [flattenedData, assetId, assetsList]);

	useEffect(() => {
		setUpdateState({});
		// A fresh list means the pasted highlights no longer refer to anything.
		setPastedCells({});
		setPasteReview(null);
		setPasteResolutions({});
		if (listItems?.length > 0) {
			let temp = {};
			listItems?.forEach((prod) => {
				prod?.variants?.forEach((variant) => {
					const unit = variant?.unit ?? prod?.unit;
					temp = {
						...temp,
						[prod?.id]: {
							...(temp?.[prod?.id] || {}),
							[variant?.variantId]: {
								threshold: toDisplayValue(
									variant?.threshold,
									unit
								),
								maxStock: toDisplayValue(
									variant?.maxStock,
									unit
								),
								recommendedStock: toDisplayValue(
									variant?.recommendedStock,
									unit
								),
							},
						},
					};
				});
			});
			setUpdateState(temp);
		}
	}, [data]);

	const refetchList = () => {
		// Same gate as the effect above — nothing to refetch until both pickers
		// are answered.
		if (!selectedStore || (isProcess && !processId)) {
			return;
		}
		fetchVariantsThreshold({
			entity: ENTITY,
			req: {
				params: {
					...listParams,
					page,
					limit: pageSize,
				},
			},
			method: 'LIST_DATA',
			showNotification: true,
		});
	};

	const handleImport = async (uploadFile) => {
		// The CSV upload endpoint has no process branch — it would write the
		// store table. Guarded here as well as by hiding the button.
		if (isProcess) {
			notificationServices.generateNotification({
				type: 'error',
				message: 'CSV upload is not supported for process level thresholds. Use Paste Excel instead.',
			});
			return;
		}
		try {
			setIsLoading(true);
			// Kg / Ltr rows in the uploaded sheet are converted back to g / ml;
			// g / ml rows are left as-is.
			const convertedFile = convertUploadedThresholdCsv(uploadFile?.file);
			const response =
				await variantsThresholdServices.uploadVariantsThresholdData({
					data: {
						variantThreshold: convertedFile,
						...(assetId && { assetId }),
					},
				});
			if (response?.statusCode === 200) {
				notificationServices.generateNotification({
					type: 'success',
					message: response?.message,
				});
				refetchList();
			} else {
				notificationServices.generateNotification({
					type: 'error',
					message: response?.message,
				});
			}
		} catch (error) {
			notificationServices.generateNotification({
				type: 'error',
				message: 'Some error occured while uploading data',
			});
		} finally {
			setIsLoading(false);
		}
	};

	const uploadVariantsThresholdData = () => {
		toggleUploadDataModal(true, {
			heading: 'Upload Variant Threshold and Recommended Stock',
			handleImport,
		});
	};

	const handleExcelDownload = useCallback((header, fileData, fileName) => {
		const commonColumns = [];
		const downloadBtn = document.createElement('a');
		const csvData = [[...commonColumns, ...header], ...fileData];

		const csv = `${csvData
			.map((e) => {
				return e.join(',');
			})
			.join('\n ')}`;

		const encodedValue = new Blob([csv], {
			type: 'text/csv',
		});
		downloadBtn.href = window.URL.createObjectURL(encodedValue);
		downloadBtn.target = '_blank';
		downloadBtn.download = fileName;
		downloadBtn.click();
	}, []);

	// The grid holds one page; an export of one page would be a silent
	// regression, so walk the API to the end of the result set first.
	const fetchAllMatchingRows = async () => {
		const collected = [];
		const limit = 500; // MAX_PAGE_SIZE on /threshold/list
		let current = 1;
		for (;;) {
			// eslint-disable-next-line no-await-in-loop
			const res = await variantsThresholdServices.gateway({
				req: { params: { ...listParams, page: current, limit } },
				method: 'LIST_DATA',
			});
			const items = toListRows(res?.data);
			collected.push(...items);
			const total = res?.data?.pagination?.total ?? 0;
			const rowsSoFar = collected.reduce((count, prod) => {
				return count + (prod?.variants?.length ?? 0);
			}, 0);
			if (items.length === 0 || rowsSoFar >= total) break;
			current += 1;
		}
		return collected;
	};

	const handleExport = async () => {
		let headers = [
			'Item Code',
			'UY Item Id',
			'Store Id',
			'Store Code',
			'Store Name',
			'Product Category',
			'SKU Type',
			'Product Name',
			'Variant Name',
			'Unit',
			'Threshold',
			'Recommended Stock',
			'Max Stock',
		];
		const assetData = assetsList?.find((ele) => ele?.value === assetId);
		const processData = processList?.find((ele) => ele?.value === processId);
		if (isProcess) {
			headers = ['Process Id', 'Process Name', ...headers];
		} else if (assetId) {
			headers = ['Asset Id', 'Asset Name', ...headers];
		}
		const totalValues = [];
		const exportData = [];
		const selectedStoreNames = [];
		let exportRows = [];
		try {
			setIsLoading(true);
			exportRows = await fetchAllMatchingRows();
		} catch (error) {
			notificationServices.generateNotification({
				type: 'error',
				message: 'Could not fetch the full list to export',
			});
			return;
		} finally {
			setIsLoading(false);
		}
		exportRows?.forEach((product) => {
			product?.variants?.forEach((variant) => {
				exportData.push({
					clientVariantId: variant?.clientVariantId,
					variantId: variant?.variantId,
					storeId: product?.storeId,
					clientStoreId: product?.clientStoreId,
					storeName: product?.storeName,
					productCategory: product?.category,
					skuType: product?.skuType,
					productName: product?.productName?.split(',').join(' '),
					variantName: variant?.variantName,
						unitLabel: getUnitMeta(variant?.unit ?? product?.unit).label,
					threshold: toDisplayValue(variant?.threshold, variant?.unit ?? product?.unit),
					recommendedStock: toDisplayValue(variant?.recommendedStock, variant?.unit ?? product?.unit),
					maxStock: toDisplayValue(variant?.maxStock, variant?.unit ?? product?.unit),
					assetId: assetData?.value,
					assetName: assetData?.label,
				});
				selectedStoreNames.push(product?.storeName);
			});
		});
		exportData?.forEach((product) => {
			let row = [
				product?.clientVariantId,
				product?.variantId,
				product?.storeId,
				product?.clientStoreId,
				product?.storeName,
				product?.productCategory,
				product?.skuType,
				product?.productName,
				product?.variantName,
					product?.unitLabel,
				product?.threshold,
				product?.recommendedStock,
				product?.maxStock,
			];
			if (isProcess) {
				row = [processData?.value, processData?.label, ...row];
			} else if (assetId) {
				row = [assetData?.value, assetData?.label, ...row];
			}
			totalValues?.push(row);
		});
		const storeNames = [...new Set(selectedStoreNames)];
		handleExcelDownload(
			headers,
			totalValues,
			isProcess
				? `Process Threshold of Products for ${processData?.label ?? 'Process'} (${storeNames?.join(',')})`
				: `Variant Threshold of Products for Stores ${storeNames?.join(',')}`
		);
	};

	const onInputChange = (val, prodId, varId, field) => {
		// Allow values up to 3 decimal places only.
		if (val !== '' && !/^\d*\.?\d{0,3}$/.test(val)) {
			return;
		}
		setUpdateState((prev) => {
			return {
				...prev,
				[prodId]: {
					...prev?.[prodId],
					[varId]: {
						...prev?.[prodId]?.[varId],
						[field]: val,
					},
				},
			};
		});
	};

	/* ---------------------------------------------------------------------- */
	/* Paste from Excel                                                       */
	/* ---------------------------------------------------------------------- */

	// Matching runs over the whole store list, not `filteredData`, so an active
	// search box never silently drops rows from the paste. `id` is deliberately
	// the uniqueKey (not the product id) so a pasted SKU can never collide with
	// a numeric product id during SKU matching.
	const pasteMatchRows = useMemo(() => {
		return flattenedData.map((item) => {
			return {
				...item,
				id: item?.uniqueKey,
				productId: item?.id,
				skuCode: item?.clientVariantId,
			};
		});
	}, [flattenedData]);

	// Shape the fuzzy matcher expects: product -> variants.
	const pasteProductList = useMemo(() => {
		const byProduct = new Map();
		flattenedData.forEach((item) => {
			if (!byProduct.has(item?.id)) {
				byProduct.set(item?.id, {
					id: item?.id,
					productName: item?.productName,
					variants: [],
				});
			}
			byProduct.get(item?.id).variants.push({
				id: item?.variantId,
				displayName: item?.variantName,
				clientVariantId: item?.clientVariantId,
				isDefault: true,
			});
		});
		return [...byProduct.values()];
	}, [flattenedData]);

	// Writes the resolved rows into updateState (display units) and remembers
	// which cells were touched. Returns how many rows actually took a value.
	const applyPastedRows = (entries) => {
		const changes = [];
		entries.forEach(({ row, values, unit }) => {
			const prodId = row?.productId;
			const variantId = row?.variantId;
			if (prodId == null || variantId == null) return;
			const converted = {};
			PASTE_FIELDS.forEach((field) => {
				const value = toDisplayFromPastedUnit(
					values?.[field],
					unit,
					row?.unit
				);
				if (value !== null) {
					converted[field] = value;
				}
			});
			if (Object.keys(converted).length === 0) return;
			changes.push({
				prodId,
				variantId,
				uniqueKey: row?.uniqueKey,
				values: converted,
			});
		});
		if (changes.length === 0) return 0;
		setUpdateState((prev) => {
			const next = { ...prev };
			changes.forEach(({ prodId, variantId, values }) => {
				next[prodId] = {
					...(next?.[prodId] || {}),
					[variantId]: {
						...(next?.[prodId]?.[variantId] || {}),
						...values,
					},
				};
			});
			return next;
		});
		setPastedCells((prev) => {
			const next = { ...prev };
			changes.forEach(({ uniqueKey, values }) => {
				next[uniqueKey] = { ...(next?.[uniqueKey] || {}) };
				Object.keys(values).forEach((field) => {
					next[uniqueKey][field] = true;
				});
			});
			return next;
		});
		return changes.length;
	};

	const notifyPasted = (count, skipped) => {
		notificationServices.generateNotification({
			type: count > 0 ? 'success' : 'warning',
			message: count > 0
				? `Pasted values for ${count} variant${count > 1 ? 's' : ''}${
					skipped ? `, ${skipped} skipped` : ''
				}. Hit Submit to save.`
				: 'No values were applied from the clipboard.',
		});
	};

	const handlePasteFromClipboard = async () => {
		if (!selectedStore) {
			notificationServices.generateNotification({
				type: 'warning',
				message: 'Select a store before pasting.',
			});
			return;
		}
		if (isProcess && !processId) {
			notificationServices.generateNotification({
				type: 'warning',
				message: 'Select a process before pasting.',
			});
			return;
		}
		// Matching only ever runs against the rows currently loaded — with the
		// server paging results, that's just the open page, not the full
		// filtered list. Send the user to "All" first rather than silently
		// matching a fraction of a larger paste.
		if (totalRows > 0 && pageSize < totalRows) {
			notificationServices.generateNotification({
				type: 'warning',
				message: `Only the ${pageSize} loaded rows can be matched. Set the page size to "All (${totalRows})" before pasting a list that spans more than one page.`,
			});
			return;
		}
		if (!navigator?.clipboard?.readText) {
			notificationServices.generateNotification({
				type: 'error',
				message: 'Clipboard read is not supported in this browser. Try Chrome/Edge.',
			});
			return;
		}
		let text = '';
		try {
			text = await navigator.clipboard.readText();
		} catch (error) {
			notificationServices.generateNotification({
				type: 'error',
				message: 'Could not read the clipboard. Allow clipboard access and copy the rows again.',
			});
			return;
		}
		if (!text?.trim()) {
			notificationServices.generateNotification({
				type: 'error',
				message: 'Clipboard is empty. Copy the rows from Excel first.',
			});
			return;
		}
		const parsed = parsePastedTable(
			text,
			PASTE_THRESHOLD_HEADERS,
			PASTE_EXTRA_COLUMNS,
			PASTE_SKU_HEADERS
		);
		if (!parsed.length) {
			notificationServices.generateNotification({
				type: 'warning',
				message: 'No valid rows found in the clipboard (expected: product name + Threshold / Max Stock / Recommended Stock).',
			});
			return;
		}
		const matched = [];
		const ambiguous = [];
		const notFound = [];
		parsed.forEach((pastedRow) => {
			const entry = {
				name: cleanPastedName(pastedRow?.name) || pastedRow?.name,
				sku: pastedRow?.sku,
				unit: pastedRow?.unit,
				values: {
					threshold: pastedRow?.qty,
					maxStock: pastedRow?.maxStock,
					recommendedStock: pastedRow?.recommendedStock,
				},
			};
			const { row, candidates } = matchPastedRowToProduct({
				name: pastedRow?.name,
				sku: pastedRow?.sku,
				flattenedProducts: pasteMatchRows,
				productList: pasteProductList,
				fuzzyMatcher: matchIngredientToProduct,
			});
			if (row) {
				matched.push({ ...entry, row });
			} else if (candidates?.length) {
				ambiguous.push({ ...entry, candidates });
			} else {
				notFound.push(entry);
			}
		});

		// Clean paste -> apply straight away; anything unresolved goes to the
		// review modal so the user decides.
		if (!ambiguous.length && !notFound.length) {
			notifyPasted(applyPastedRows(matched), 0);
			return;
		}
		setPasteResolutions({});
		setPasteReview({ matched, ambiguous, notFound });
	};

	const closePasteReview = () => {
		setPasteReview(null);
		setPasteResolutions({});
	};

	const handlePasteReviewApply = () => {
		if (!pasteReview) return;
		const resolved = (pasteReview.ambiguous || [])
			.map((item, index) => {
				const variantId = pasteResolutions?.[index];
				if (!variantId) return null;
				const row = pasteMatchRows.find(
					(ele) => String(ele?.variantId) === String(variantId)
				);
				return row ? { ...item, row } : null;
			})
			.filter(Boolean);
		const entries = [...(pasteReview.matched || []), ...resolved];
		const skipped = (pasteReview.notFound?.length || 0)
			+ ((pasteReview.ambiguous?.length || 0) - resolved.length);
		const count = applyPastedRows(entries);
		closePasteReview();
		notifyPasted(count, skipped);
	};

	const TABLE_COLS = useMemo(() => {
		if (assetId) {
			return [
				// {
				// 	title: 'Asset Id',
				// 	config: {
				// 		type: 'DATA',
				// 	},
				// },
				// {
				// 	title: 'Asset Name',
				// 	config: {
				// 		type: 'DATA',
				// 	},
				// },
				...TABLE_COLUMNS,
			];
		}
		return TABLE_COLUMNS;
	}, [assetId]);

	const SPECIAL_ITEMS = {
		Unit: (unit) => {
			return getUnitMeta(unit).label ?? '-';
		},
		Threshold: (unknown, item) => {
			// ListTable might pass value as first arg, but we use uniqueKey for identification
			// The second arg is usually the row data
			const rowData =
				item || flattenedData.find((ele) => ele?.uniqueKey === unknown);
			const { id: prodId, variantId, uniqueKey } = rowData || {};
			const value = updateState?.[prodId]?.[variantId]?.threshold ?? '';
			return generateInput(
				value,
				(e) =>
					onInputChange(e.target.value, prodId, variantId, 'threshold'),
				pastedCells?.[uniqueKey]?.threshold
			);
		},
		'Max Stock': (unknown, item) => {
			const rowData =
				item || flattenedData.find((ele) => ele?.uniqueKey === unknown);
			const { id: prodId, variantId, uniqueKey } = rowData || {};
			const value = updateState?.[prodId]?.[variantId]?.maxStock ?? '';
			return generateInput(
				value,
				(e) =>
					onInputChange(e.target.value, prodId, variantId, 'maxStock'),
				pastedCells?.[uniqueKey]?.maxStock
			);
		},
		'Recommended Stock': (unknown, item) => {
			const rowData =
				item || flattenedData.find((ele) => ele?.uniqueKey === unknown);
			const { id: prodId, variantId, uniqueKey } = rowData || {};
			const value =
				updateState?.[prodId]?.[variantId]?.recommendedStock ?? '';
			return generateInput(
				value,
				(e) =>
					onInputChange(
						e.target.value,
						prodId,
						variantId,
						'recommendedStock'
					),
				pastedCells?.[uniqueKey]?.recommendedStock
			);
		},
	};

	// const selectStore = (e) => {
	// setSelectedStore(e.target.value);
	// };

	// Cells the user actually changed, against the rows currently loaded.
	// Submit sends these; the page-change guard asks before throwing them away.
	const pendingEdits = useMemo(() => {
		let editValues = {};
		listItems?.forEach((prod) => {
			prod?.variants?.forEach((variant) => {
				const current =
					updateState?.[prod?.id]?.[variant?.variantId] || {};
				const original = variant;
				// eslint-disable-next-line eqeqeq
				if (
					toBaseValue(current?.threshold, variant?.unit ?? prod?.unit) != original?.threshold ||
					toBaseValue(current?.maxStock, variant?.unit ?? prod?.unit) != original?.maxStock ||
					toBaseValue(current?.recommendedStock, variant?.unit ?? prod?.unit) != original?.recommendedStock
				) {
					editValues = {
						...editValues,
						[prod?.id]: {
							...editValues?.[prod?.id],
							[variant?.variantId]: {
								threshold: toBaseValue(current?.threshold, variant?.unit ?? prod?.unit),
								maxStock: toBaseValue(current?.maxStock, variant?.unit ?? prod?.unit),
								recommendedStock: toBaseValue(current?.recommendedStock, variant?.unit ?? prod?.unit),
							},
						},
					};
				}
			});
		});
		return editValues;
	}, [listItems, updateState]);

	const hasPendingEdits = Object.keys(pendingEdits ?? {}).length > 0;

	// Leaving a page drops its unsaved cells - the next page's rows replace
	// updateState wholesale, and Submit only ever sends the loaded page.
	const handlePageChange = (nextPage) => {
		if (nextPage === page) return;
		if (
			hasPendingEdits &&
			// eslint-disable-next-line no-alert
			!window.confirm(
				'You have unsaved threshold changes on this page. Leaving the page will discard them. Continue?'
			)
		) {
			return;
		}
		setPage(nextPage);
	};

	const handlePageSizeChange = (nextLimit) => {
		if (nextLimit === pageSize) return;
		if (
			hasPendingEdits &&
			// eslint-disable-next-line no-alert
			!window.confirm(
				'You have unsaved threshold changes on this page. Changing the page size will discard them. Continue?'
			)
		) {
			return;
		}
		setPageSize(nextLimit);
	};

	const onSubmit = async () => {
		const editValues = pendingEdits;
		if (isProcess && !processId) {
			notificationServices.generateNotification({
				type: 'warning',
				message: 'Select a process before saving.',
			});
			return;
		}
		try {
			const response = await variantsThresholdServices.editThreshold({
				params: {
					storeId: selectedStore,
					...scopeParams,
				},
				data: {
					editValues,
				},
			});
			if (response?.statusCode === 200) {
				notificationServices.generateNotification({
					type: 'success',
					message: response?.message,
				});
				refetchList();
			} else {
				notificationServices.generateNotification({
					type: 'error',
					message: response?.message || 'Update failed',
				});
			}
		} catch (error) {
			notificationServices.generateNotification({
				type: 'error',
				message: 'Some error occured',
			});
		}
	};

	const handleScroll = useCallback(() => {
		// if (listRef?.current) {
		// const { scrollTop, scrollHeight, clientHeight } = listRef.current;
		// if (Math.floor(scrollTop + clientHeight) === scrollHeight) {
		// console.log('CHeckkk');
		// setPageCount((prev) => {
		// return {
		// ...prev,
		// currentPage: prev?.currentPage + 1,
		// };
		// });
		// }
		// }
	}, []);

	const BASE_STORES = useMemo(() => {
		const tempStoreList = stores?.map((ele) => {
			return {
				...ele,
				label: ele?.name,
				role: ele?.type,
				value: ele?.id,
			};
		});
		return tempStoreList;
	}, [stores]);

	return (
		<div className={styles.root}>
			<BackdropLoader showProgress isLoading={isLoading} />
			<UploadDataModal
				show={showUploadDataModal}
				handleImport={handleImport}
				heading={payload?.heading}
			/>
			<PasteThresholdReviewModal
				open={!!pasteReview}
				matchedCount={pasteReview?.matched?.length || 0}
				ambiguous={pasteReview?.ambiguous || []}
				notFound={pasteReview?.notFound || []}
				resolutions={pasteResolutions}
				onResolve={(index, variantId) => {
					setPasteResolutions((prev) => {
						return {
							...prev,
							[index]: variantId,
						};
					});
				}}
				onApply={handlePasteReviewApply}
				onClose={closePasteReview}
			/>
			<div className={styles.header}>
				<div className={styles.filter}>
					<Input
						value={searchQuery}
						onChange={(value) => {
							setSearchQuery(value);
						}}
						style={{
							width: 200,
							borderRadius: 25,
							border: '1px solid #000',
						}}
						placeholder='Search Product / SkuCode'
					/>
					<div className={styles['store-container']}>
						{/* <TextField
							label='Select Store'
							className={styles['store-select']}
							select
							value={selectedStore}
							onChange={selectStore}
						>
							{stores?.map((ele) => {
								return (
									<MenuItem key={ele.id} value={ele.id}>
										{ele?.name}
									</MenuItem>
								);
							})}
						</TextField> */}
						<SelectPicker
							data={BASE_STORES}
							style={{
								width: 224,
							}}
							groupBy='role'
							placeholder='Select Store'
							onChange={(storeId) => {
								setSelectedStore(storeId);
							}}
							renderMenuItem={(_) => {
								return (
									<div>
										<i className='rs-icon rs-icon-user' />
										{_}
									</div>
								);
							}}
							renderMenuGroup={(_) => {
								return (
									<div>
										<i className='rs-icon rs-icon-group' />
										{_}
									</div>
								);
							}}
							value={selectedStore}
							cleanable={false}
							renderValue={(value, item) => {
								return (
									<div className='rsuite-select-picker-render-value'>
										{item?.label}
									</div>
								);
							}}
						/>
						{!selectedStore && (
							<p>
								{isProcess
									? 'Select Store to get Processes'
									: 'Select Store to get Variants threshold'}
							</p>
						)}
					</div>
					{isProcess && (
						<div className={styles['store-container']}>
							<SelectPicker
								data={processList}
								value={processId}
								onChange={(value) => setProcessId(value)}
								placeholder='Select Process'
								searchable
								cleanable={false}
								disabled={!selectedStore}
								loading={processLoading}
								style={{ width: 260 }}
							/>
							{selectedStore && !processId && (
								<p>Select Process to get its threshold</p>
							)}
						</div>
					)}
					{!isProcess && assetsList?.length > 0 && (
						<SelectPicker
							data={assetsList}
							value={assetId}
							onChange={(value) => setAssetId(value)}
							placeholder='Select Asset'
							searchable
							cleanable
							loading={assetsLoading}
							style={{ width: 200 }}
						/>
					)}
					{/* <TextField
						label='Select SKU Type'
						className={styles.selector}
						select
						value={selectedSkuType}
						onChange={(e) => {
							setSelectedSkuType(e?.target.value);
						}}
					>
						{skuTypes?.map((ele) => {
							return (
								<MenuItem key={ele} value={ele}>
									{ele}
								</MenuItem>
							);
						})}
					</TextField> */}
					<div className={styles['store-container']}>
						<SelectPicker
							data={skuTypes?.map((ele) => {
								return {
									label: ele,
									value: ele,
								};
							})}
							value={selectedSkuType}
							onChange={(skuTypeValue) => {
								setSelectedSkuType(skuTypeValue);
							}}
							cleanable={false}
							searchable={false}
							style={{
								width: 150,
							}}
							placeholder='SKU Type'
						/>
						{/* No prompt: an unset SKU Type simply means every
							type, the same as picking ALL. */}
					</div>
				</div>
				<div className='flex flex-row gap-2'>
					{selectedStore && (!isProcess || processId) && level !== 1 && (
						<Button
							variant='outlined'
							className={styles['paste-button']}
							startIcon={<ClipboardPaste size={15} />}
							onClick={handlePasteFromClipboard}
							title='Paste rows copied from Excel. Include a header row with Product Name (or Item Code) plus any of: Threshold, Max Stock, Recommended Stock. Unit is optional — Kg / Ltr values are taken as shown in the grid.'
						>
							Paste Excel
						</Button>
					)}
					{/* CSV upload writes the store table only — no process branch
					in product_bulk_upload.uploadVariantThreshold. */}
					{!isProcess && selectedStore && (nonInternal || isInternal) && (
						<Button
							variant='contained'
							className={styles.button}
							onClick={uploadVariantsThresholdData}
						>
							Update Threshold
						</Button>
					)}
					{Object.keys(updateState ?? {})?.length > 0 &&
						(!isProcess || processId) &&
						level !== 1 && (
							<Button
								variant='contained'
								className={styles.button}
								onClick={onSubmit}
							>
								Submit
							</Button>
						)}
				</div>
			</div>
			{totalRows > 0 && (
				<div className='flex justify-end items-center gap-3 px-4 py-2'>
					<Pagination
						prev
						next
						first
						last
						ellipsis
						boundaryLinks
						maxButtons={7}
						size='sm'
						layout={['total', '-', '|', 'pager']}
						total={totalRows}
						limit={pageSize}
						activePage={page}
						onChangePage={handlePageChange}
					/>
					<SelectPicker
						data={limitSelectData}
						value={pageSize}
						onChange={handlePageSizeChange}
						cleanable={false}
						searchable={false}
						placement='bottomEnd'
						style={{ width: 140 }}
					/>
				</div>
			)}
			<div
				onScroll={handleScroll}
				ref={listRef}
				className={styles['list-container']}
			>
				<ListTable
					useWindowScroll={false}
					onScroll={handleScroll}
					colMap={COLMAP}
					columns={[...TABLE_COLS, ...SPECIAL_COLUMNS]}
					data={filteredData}
					fixedHeaderTop={0}
					listRef={listRef}
					showTotal
					actions={[...ACTION_COLUMNS]}
					special={SPECIAL_ITEMS}
					classname={styles.table}
					onExportClick={handleExport}
					onImportClick={handleImport}
				/>
			</div>
		</div>
	);
};

const mapStateToProps = (state, ownProps) => {
	const entity =
		ownProps?.thresholdLevel === 'PROCESS'
			? 'PROCESS_THRESHOLD'
			: 'VARIANTS_THRESHOLD';
	const data = state.form[entity].data.LIST_DATA;
	const stores = state?.form?.STORES?.data?.LIST;
	const { uploadDataModal } = state.modal;
	const showUploadDataModal = uploadDataModal?.show;
	const payload = uploadDataModal?.data;
	const { userPermissions, navState } = state?.navigation;
	const { userList } = state?.auth;
	return {
		data,
		stores,
		showUploadDataModal,
		payload,
		userPermissions,
		navState,
		userList,
	};
};

const mapDispatchToProps = {
	fetchVariantsThreshold: formActions.gateway,
	fetchStoresList: formActions.gateway,
	updateVariantsThresholdList: formActions.updateFields,
	toggleUploadDataModal: modalActions.toggleUploadDataModal,
	fetchUserList: authActions?.fetchUserList,
};

export default connect(
	mapStateToProps,
	mapDispatchToProps
)(VariantsThresholdListPage);
